// Éléments communs des écrans. Minimalisme de la DA : texte à gauche, une
// colonne étroite, des filets plutôt que des cartes, aucun badge.
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type Variante = 'principal' | 'secondaire' | 'danger';

const VARIANTES: Record<Variante, string> = {
  principal: 'bg-accent text-sur-accent hover:opacity-90',
  secondaire: 'border border-filet text-texte hover:border-accent',
  danger: 'border border-alerte text-alerte hover:bg-alerte hover:text-fond',
};

export function Bouton({
  variante = 'principal',
  grand = false,
  className = '',
  ...props
}: Readonly<ButtonHTMLAttributes<HTMLButtonElement> & { variante?: Variante; grand?: boolean }>) {
  const taille = grand ? 'w-full py-5 text-2xl font-semibold' : 'px-5 py-3 text-base font-medium';
  return (
    <button
      type="button"
      className={`${VARIANTES[variante]} ${taille} rounded-lg transition-opacity disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      {...props}
    />
  );
}

export function Titre({ children }: Readonly<{ children: ReactNode }>) {
  return <h1 className="text-2xl leading-tight font-semibold">{children}</h1>;
}

export function SousTitre({ children }: Readonly<{ children: ReactNode }>) {
  return <h2 className="mt-10 text-lg font-semibold">{children}</h2>;
}

export function Texte({
  children,
  discret = false,
}: Readonly<{ children: ReactNode; discret?: boolean }>) {
  return (
    <p
      className={`mt-3 leading-relaxed ${discret ? 'text-secondaire text-sm' : 'text-texte-long'}`}
    >
      {children}
    </p>
  );
}

/** Liste à filets (DA : « une liste, pas des cartes »). */
export function Lignes({ children, label }: Readonly<{ children: ReactNode; label?: string }>) {
  return (
    <ul aria-label={label} className="divide-filet border-filet mt-4 divide-y border-y">
      {children}
    </ul>
  );
}

export function Chargement() {
  const { t } = useTranslation();
  return (
    <p role="status" className="text-secondaire">
      {t('app.chargement')}
    </p>
  );
}

export function Erreur({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <p role="alert" className="text-alerte mt-4">
      {children}
    </p>
  );
}

/** Adresse publiée dans Confidentialité et en pied de page (WCAG 3.2.6, aide constante). */
export const CONTACT = 'contact@sensanetwork.com';

/** Ligne d'aide sous un écran où l'on peut rester bloqué : à qui écrire. */
export function AideContact({ texte }: Readonly<{ texte?: string }>) {
  const { t } = useTranslation();
  return (
    <p className="text-secondaire mt-8 text-sm leading-relaxed">
      {texte ?? t('aide.contact')}{' '}
      <a href={`mailto:${CONTACT}`} className="text-texte underline underline-offset-4">
        {CONTACT}
      </a>
      .
    </p>
  );
}

/** Erreur de chargement : ce qui ne va pas, et un bouton pour réessayer (AgDR-0241, règle 4). */
export function ErreurReseau({ onReessayer }: Readonly<{ onReessayer: () => void }>) {
  const { t } = useTranslation();
  return (
    <section>
      <Erreur>{t('erreur.reseau')}</Erreur>
      <Bouton variante="secondaire" className="mt-4" onClick={onReessayer}>
        {t('erreur.reessayer')}
      </Bouton>
      <AideContact />
    </section>
  );
}
